import React from 'react';

import { KEYDOWN, } from '../../Shared/utils/eventNames'
import { ESCAPE, } from '../../Shared/utils/keyNames'

const CORE_REPORTS_LINKS = [
  {
    label: 'Activity Summary Report',
    url: '/teachers/classrooms/scorebook'
  },
  {
    label: 'Activity Analysis Report',
    url: '/teachers/progress_reports/diagnostic_reports/activity_packs'
  },
]

const DIAGNOSTIC_REPORTS_LINKS = [
  {
    label: 'My Diagnostics',
    url: '/teachers/progress_reports/diagnostic_reports/diagnostics'
  },
  {
    label: 'Diagnostic Insights',
    url: '/teachers/progress_reports/diagnostic_insights',
    new: true
  },
]

const PREMIUM_REPORTS_LINKS = [
  {
    label: 'Activity Scores Report',
    url: '/teachers/progress_reports/activities_scores_by_classroom'
  },
  {
    label: 'Skills Report',
    url: '/teachers/progress_reports/skills_report',
    new: true
  },
  {
    label: 'Standards Report',
    url: '/teachers/progress_reports/standards_report',
    new: true
  },
  {
    label: 'Data Export',
    url: '/teachers/progress_reports/activity_sessions'
  },
]

const CURRICULUM_SPECIFIC_REPORTS_LINKS = (showSocialStudiesTab, showScienceTab, socialStudiesDashboardLink, showQuillForTexasTab, showLiteratureTab, literatureDashboardLink) => {
  const socialStudiesTab = showSocialStudiesTab && {
    label: 'Social Studies Dashboard',
    url: socialStudiesDashboardLink
  }

  const scienceTab = showScienceTab && {
    label: 'Interdisciplinary Science Dashboard',
    url: '/teachers/progress_reports/interdisciplinary-science/building-ai-knowledge'
  }

  const literatureTab = showLiteratureTab && {
    label: 'Literature Dashboard',
    url: literatureDashboardLink,
    new: true
  }

  const quillForTexasTab = showQuillForTexasTab && {
    label: 'Quill For Texas Dashboard',
    url: '/teachers/progress_reports/quill-for-texas'
  }

  return [
    literatureTab,
    socialStudiesTab,
    scienceTab,
    quillForTexasTab
  ].filter(Boolean)
}

const DropdownTab = ({ activeTab, tabName, dropdownItems, dropdownId }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef(null)
  const dropdownRef = React.useRef(null);
  const buttonRef = React.useRef(null);

  React.useEffect(() => {
    document.addEventListener(KEYDOWN, handleKeyDown);
    return () => {
      document.removeEventListener(KEYDOWN, handleKeyDown);
    };
  }, [dropdownRef, isOpen]);

  React.useEffect(() => {
    const container = containerRef.current;

    function handleFocusOut(event) {
      if (event.relatedTarget && !container.contains(event.relatedTarget)) {
        closeDropdown();
      }
    }

    function handleMouseDown(event) {
      if (!container.contains(event.target)) {
        closeDropdown();
      }
    }

    if (container) {
      container.addEventListener('focusout', handleFocusOut);
      document.addEventListener('mousedown', handleMouseDown);
    }

    return () => {
      if (container) {
        container.removeEventListener('focusout', handleFocusOut);
        document.removeEventListener('mousedown', handleMouseDown);
      }
    };
  }, [isOpen]);


  const activeClass = activeTab === tabName ? 'active' : '';
  const openClass = isOpen ? 'open' : '';

  function toggleDropdown() {
    if (isOpen) {
      closeDropdown()
    } else {
      setIsOpen(true);

      // setTimeout here is a workaround to make sure that the dropdownRef is actually available in the DOM by the time this executes (it pushes execution to the end of the event queue, after the rerender)
      setTimeout(() => {
        const firstItem = dropdownRef.current.querySelector('a');
        if (firstItem) firstItem.focus();
      }, 0);
    }
  }

  function closeDropdown() {
    if (!isOpen) { return }

    setIsOpen(false);
    // Return focus to the button when dropdown closes
    if (buttonRef.current) {
      buttonRef.current.focus();
    }
  }

  function handleKeyDown(event) {
    if (event.key === ESCAPE) {
      closeDropdown();
    }
  }

  function renderDropdown() {
    return (
      <div className="dropdown" id={dropdownId} ref={dropdownRef} role="menu">
        {dropdownItems.map((item) => (
          <a className="focus-on-light" href={item.url} key={item.label} onClick={closeDropdown} role="menuitem">
            {item.label}
            {item.new ? <span className="gold-new-tag">NEW</span> : null}
          </a>
        ))}
      </div>
    );
  }

  return (
    <li className="dropdown-tab" ref={containerRef}>
      <button
        aria-controls={dropdownId}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={`focus-on-dark ${activeClass} ${openClass}`}
        onClick={toggleDropdown}
        ref={buttonRef}
        type="button"
      >
        <span>{tabName}</span>
      </button>
      {isOpen ? renderDropdown() : null}
    </li>
  );
};

const ALL_REPORTS = 'All Reports'
const CORE_REPORTS = 'Core Reports'
const DIAGNOSTIC_REPORTS = 'Diagnostic Reports'
const PREMIUM_REPORTS = 'Premium Reports'
const CURRICULUM_SPECIFIC_REPORTS = 'Curriculum-Specific Reports'
const SOCIAL_STUDIES_DASHBOARD = 'Social Studies Dashboard'
const INTERDISCIPLINARY_SCIENCE_DASHBOARD = 'Interdisciplinary Science Dashboard'
const LITERATURE_DASHBOARD = 'Literature Dashboard'
const QUILL_FOR_TEXAS_DASHBOARD = 'Quill For Texas Dashboard'

const ProgressReportsSubnavLinks = ({ showSocialStudiesTab, showScienceTab, socialStudiesDashboardLink, showQuillForTexasTab, showLiteratureTab, literatureDashboardLink }) => {
  const path = window.location
  const [activeTab, setActiveTab] = React.useState<string>('');

  // Determine how many curriculum-specific tabs are enabled
  const enabledCurriculumTabs = [showSocialStudiesTab, showScienceTab, showLiteratureTab, showQuillForTexasTab].filter(Boolean).length
  const showCurriculumDropdown = enabledCurriculumTabs >= 2
  const showSingleCurriculumTab = enabledCurriculumTabs === 1

  React.useEffect(() => {
    if (path.href) {
      determineActiveTab()
    }
  }, [path.href, showCurriculumDropdown])

  function determineActiveTab() {
    const { href } = path;

    const coreReportPaths = ['/scorebook', '/activity_packs', 'diagnostic_reports/u/']
    const diagnosticPaths = ['/diagnostic_reports/diagnostics', '/diagnostic_insights']

    if (coreReportPaths.find(path => href.includes(path))) {
      setActiveTab(CORE_REPORTS)
    } else if (diagnosticPaths.find(path => href.includes(path))) {
      setActiveTab(DIAGNOSTIC_REPORTS)
    } else if (PREMIUM_REPORTS_LINKS.some(link => href.includes(link.url))) {
      setActiveTab(PREMIUM_REPORTS)
    } else if (href.includes('/social-studies')) {
      setActiveTab(showCurriculumDropdown ? CURRICULUM_SPECIFIC_REPORTS : SOCIAL_STUDIES_DASHBOARD)
    } else if (href.includes('/interdisciplinary-science')) {
      setActiveTab(showCurriculumDropdown ? CURRICULUM_SPECIFIC_REPORTS : INTERDISCIPLINARY_SCIENCE_DASHBOARD)
    } else if (href.includes('/literature')) {
      setActiveTab(showCurriculumDropdown ? CURRICULUM_SPECIFIC_REPORTS : LITERATURE_DASHBOARD)
    } else if (href.includes('/quill-for-texas')) {
      setActiveTab(showCurriculumDropdown ? CURRICULUM_SPECIFIC_REPORTS : QUILL_FOR_TEXAS_DASHBOARD)
    } else if (href.includes('/landing_page')) {
      setActiveTab(ALL_REPORTS)
    }
  }

  function determineClassName(tabName) {
    if (tabName === activeTab) {
      return 'focus-on-dark active'
    }

    return 'focus-on-dark'
  }

  function renderCurriculumSpecificTab() {
    if (showCurriculumDropdown) {
      return (
        <DropdownTab
          activeTab={activeTab}
          dropdownId="curriculum-specific-reports-dropdown"
          dropdownItems={CURRICULUM_SPECIFIC_REPORTS_LINKS(showSocialStudiesTab, showScienceTab, socialStudiesDashboardLink, showQuillForTexasTab, showLiteratureTab, literatureDashboardLink)}
          tabName={CURRICULUM_SPECIFIC_REPORTS}
        />
      )
    }

    if (showSingleCurriculumTab) {
      if (showSocialStudiesTab) {
        return (
          <li>
            <a className={determineClassName(SOCIAL_STUDIES_DASHBOARD)} href={socialStudiesDashboardLink}>{SOCIAL_STUDIES_DASHBOARD}</a>
          </li>
        )
      }
      if (showScienceTab) {
        return (
          <li>
            <a className={determineClassName(INTERDISCIPLINARY_SCIENCE_DASHBOARD)} href="/teachers/progress_reports/interdisciplinary-science/building-ai-knowledge">{INTERDISCIPLINARY_SCIENCE_DASHBOARD}</a>
          </li>
        )
      }
      if (showLiteratureTab) {
        return (
          <li>
            <a className={determineClassName(LITERATURE_DASHBOARD)} href={literatureDashboardLink}>{LITERATURE_DASHBOARD}</a>
          </li>
        )
      }
      if (showQuillForTexasTab) {
        return (
          <li>
            <a className={determineClassName(QUILL_FOR_TEXAS_DASHBOARD)} href="/teachers/progress_reports/quill-for-texas">{QUILL_FOR_TEXAS_DASHBOARD}</a>
          </li>
        )
      }
    }

    return null
  }

  return (
    <ul>
      <li>
        <a className={determineClassName(ALL_REPORTS)} href="/teachers/progress_reports/landing_page">{ALL_REPORTS}</a>
      </li>
      <DropdownTab
        activeTab={activeTab}
        dropdownId="core-reports-dropdown"
        dropdownItems={CORE_REPORTS_LINKS}
        tabName={CORE_REPORTS}
      />
      <DropdownTab
        activeTab={activeTab}
        dropdownId="diagnostic-reports-dropdown"
        dropdownItems={DIAGNOSTIC_REPORTS_LINKS}
        tabName={DIAGNOSTIC_REPORTS}
      />
      <DropdownTab
        activeTab={activeTab}
        dropdownId="premium-reports-dropdown"
        dropdownItems={PREMIUM_REPORTS_LINKS}
        tabName={PREMIUM_REPORTS}
      />
      {renderCurriculumSpecificTab()}
    </ul>
  )
}

export default ProgressReportsSubnavLinks
